//@ts-check
import React, { useState, Fragment, useMemo, useRef, memo, useContext } from 'react'
import { CampaignContext, formatCurrency } from '../utils'
import { useStyles } from '../style'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { AutoSizer, List, CellMeasurerCache, CellMeasurer } from 'react-virtualized'
import { groupBy, pickBy } from 'lodash'
import { useLayoutEffect } from 'react'
import { columnTitles } from '../options'
import { theme } from '../../../../../../Assets/Styles/theme'
import { CampaignCard } from './CampaignCard'
/** @import { Campaign, Country, Cities } from '../utils' */
/** @import { StatusOptions } from './Filter' */

/**
 * @param {{
 *   data: Campaign[]
 *   status: StatusOptions
 * }} props
 */
export function Board({ data, status }) {
  const [isXScrolled, setIsXScrolled] = useState(false)
  const classes = useStyles({ isXScrolled })

  const draftHeaders = Object.keys(pickBy(status.draft))
  const draftColumns = useMemo(
    () => groupBy(data, x => x.draftStep),
    [data],
  )

  const submittedHeaders = Object.keys(pickBy(status.submitted))
  const submittedColumns = useMemo(
    () => groupBy(data, x => x.internalStatus),
    [data],
  )

  /**@type import('react').MutableRefObject<HTMLDivElement | null>*/
  const scrollRef = useRef(null)

  function checkIsScrolled({ scrollWidth, scrollLeft, clientWidth }) {
    setIsXScrolled(scrollWidth - scrollLeft < clientWidth + 5)
  }

  useLayoutEffect(() => {
    if (scrollRef.current) {
      checkIsScrolled(scrollRef.current)
    }
  }, [draftHeaders, submittedHeaders])

  return (
    <Box style={{ position: 'relative' }}>
      <Box className={classes.boardOverlay} />
      <div
        ref={scrollRef}
        style={{ display: 'flex', overflowY: 'auto' }}
        className={classes.boardBorder}
        onScroll={({ currentTarget }) => {
          checkIsScrolled(currentTarget)
        }}
      >
        <Group
          title="DRAFT"
          headers={draftHeaders}
          draft
          columns={draftColumns}
        />
        <Group
          title="SUBMITTED"
          headers={submittedHeaders}
          columns={submittedColumns}
        />
      </div>
    </Box>
  )
}

export const MemoizedBoard = memo(Board)

/**
 * @param {{
 *   title: string,
 *   headers: string[],
 *   draft?: true,
 *   columns: Record<string, Campaign[]>,
 * }} props
 */
function Group({ title, headers, draft, columns }) {
  const { country } = useContext(CampaignContext);
  const sectionColumns = useMemo(
    () => headers.flatMap(header => (columns[header] ? columns[header] : [])),
    [headers, columns],
  )
  const totalCost = useMemo(
    () =>
      formatCurrency(
        sectionColumns.reduce((x, y) => x + y.totalCosts, 0),
        country.currencyName,
      ),
    [sectionColumns, country.currencyName],
  )
  const headerGroupColor = { color: draft ? '#1A2547' : '#FFFFFF' }

  if (headers.length === 0) {
    return <Fragment />
  }

  return (
    <Box
      id="head"
      style={{ flex: 1, flexGrow: headers.length }}
    >
      <Box
        style={{
          padding: 16,
          paddingLeft: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          backgroundColor: draft ? undefined : theme.colors.deepBlue,
        }}
      >
        <Typography style={{ ...headerGroupColor, fontWeight: 600, fontSize: "1rem" }}>{title}</Typography>
        <Box>
          <Typography style={{ ...headerGroupColor, fontWeight: 600, fontSize: ".75rem" }} align="right">
            {sectionColumns.length}
          </Typography>
          <Typography style={{ ...headerGroupColor, fontWeight: 600, fontSize: ".75rem" }} align="right">
            {totalCost}
          </Typography>
        </Box>
      </Box>
      <Box style={{ display: 'flex' }}>
        {headers.map((header) =>
          <Column
            key={header}
            header={header}
            rows={columns[header] || []}
          />
        )}
      </Box>
    </Box>
  )
}

/**
 * @param {{
 *   header: string,
 *   rows: Campaign[],
 * }} props
 */
function Column({ header, rows }) {
  /**@type import('react').MutableRefObject<List | null>*/
  const listRef = useRef(null)
  const { country } = useContext(CampaignContext);
  const spacing = 8
  const headerHeight = 70
  const totalCost = formatCurrency(
    rows.reduce((x, y) => x + y.totalCosts, 0),
    country.currencyName,
  )

  const cellMeasurer = new CellMeasurerCache({
    fixedWidth: true,
  })

  /** @param {import('react-virtualized').ListRowProps} props */
  function BoardRow({ key, parent, index, style }) {
    function clearHeight() {
      if (!listRef.current) return
      cellMeasurer.clear(index, 0)
      listRef.current.recomputeRowHeights(index)
    }

    return (
      <CellMeasurer key={key} parent={parent} columnIndex={0} rowIndex={index} cache={cellMeasurer} >
        <Box style={{ ...style, padding: spacing / 2 }}>
          <CampaignCard
            item={rows[index]}
            currencyName={country.currencyName}
            clearHeight={clearHeight}
          />
        </Box>
      </CellMeasurer>
    )
  }

  return (
    <Box id="column" style={{ flex: 1, minWidth: 280, height: "85vh" }}>
      <AutoSizer>
        {({ height, width }) =>
          <Box style={{ width }} >
            <Box
              id="columnHeader"
              style={{
                padding: spacing,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: headerHeight,
              }}
            >
              <Typography style={{ fontWeight: 600, fontSize: ".88rem" }}>{columnTitles[header]}</Typography>
              <Box>
                <Typography style={{ color: theme.colors.silver3, fontSize: ".75rem" }} align="right">
                  {rows.length}
                </Typography>
                <Typography style={{ color: theme.colors.silver3, fontSize: ".75rem" }} align="right">
                  {totalCost}
                </Typography>
              </Box>
            </Box>
            <List
              ref={listRef}
              height={height - (headerHeight + spacing)}
              width={width - spacing}
              rowCount={rows.length}
              rowHeight={cellMeasurer.rowHeight}
              deferredMeasurementCache={cellMeasurer}
              rowRenderer={BoardRow}
              overscanRowCount={3}
              style={{
                margin: spacing / 2,
                scrollbarWidth: "thin",
                scrollbarGutter: "stable",
              }}
            />
          </Box>
        }
      </AutoSizer>
    </Box>
  )
}
